"use client";
import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
    useRef,
} from "react";
import callApi from "@api/api";

import PlayerList from "./PlayerList/PlayerList";
import Dropdown, { DropdownOption } from "./Dropdown/Dropdown";
import Tabs from "./Tabs/Tabs";
import { transformTeamsPlayers, TransformedTeam } from "./constant";

import styles from "./Squad.module.scss";
import Shimmer from "@components/Shimmer";
import { Theme } from "@/constants/enums";
import useWindowDimensions from "@utils/useWindowDimensions";
import { generateWidgetUrl } from "@utils/widgetHelper";

export enum PlayerSkill {
    sport_player_roles_prefix_1 = "sport_player_roles_prefix_1", // Batters
    sport_player_roles_prefix_2 = "sport_player_roles_prefix_2", // Bowlers
    sport_player_roles_prefix_3 = "sport_player_roles_prefix_3", // All Rounders
    sport_player_roles_prefix_4 = "sport_player_roles_prefix_4", // Wicket Keepers
}

export enum FilterTab {
    All = "All",
    Batters = "Batters",
    Bowlers = "Bowlers",
    AllRounders = "AllRounders",
    WicketKeepers = "WicketKeepers", // New tab for wicket keepers
}

interface SquadWidgetType {
    seriesKey?: string;
    seriesName?: string;
}

// Map each tab to the allowed player skills
const tabToSkills: Record<FilterTab, PlayerSkill[]> = {
    [FilterTab.All]: [
        PlayerSkill.sport_player_roles_prefix_1,
        PlayerSkill.sport_player_roles_prefix_2,
        PlayerSkill.sport_player_roles_prefix_3,
        PlayerSkill.sport_player_roles_prefix_4,
    ],
    [FilterTab.Batters]: [PlayerSkill.sport_player_roles_prefix_1],
    [FilterTab.Bowlers]: [PlayerSkill.sport_player_roles_prefix_2],
    [FilterTab.AllRounders]: [PlayerSkill.sport_player_roles_prefix_3],
    [FilterTab.WicketKeepers]: [PlayerSkill.sport_player_roles_prefix_4],
};

const SquadWidget: React.FC<SquadWidgetType> = ({ seriesKey, seriesName }) => {
    const theme = localStorage.getItem("theme");
    const dimensions = useWindowDimensions();

    // State for transformed teams data
    const [teams, setTeams] = useState<TransformedTeam[]>([]);
    const [selectedTeam, setSelectedTeam] = useState<string>("");
    const [activeTab, setActiveTab] = useState<FilterTab>(FilterTab.All);
    const [loading, setLoading] = useState<boolean>(false);

    // Ref for player list container to control scroll
    const playerListContainerRef = useRef<HTMLDivElement>(null);

    // Derived current players based on selected team
    const currentPlayers = useMemo(() => {
        if (teams.length === 0 || !selectedTeam) return [];
        const teamData = teams.find(
            (team) => team.team_name?.toLowerCase() === selectedTeam,
        );
        return teamData ? teamData.playerlist : [];
    }, [teams, selectedTeam]);

    // Filter players based on the active tab using the mapping
    const filteredPlayers = useMemo(() => {
        if (activeTab === FilterTab.All) return currentPlayers;
        const allowedSkills = tabToSkills[activeTab];
        return currentPlayers.filter((player) =>
            allowedSkills.includes(player.player_skill as PlayerSkill),
        );
    }, [currentPlayers, activeTab]);

    // Build dropdown options from teams data.
    const dropdownOptions: DropdownOption[] = useMemo(
        () =>
            teams.map((team) => ({
                label: team.team_name,
                value: team.team_name?.toLowerCase(),
                image: team.team_image,
            })),
        [teams],
    );

    // Memoized callbacks
    const handleTeamChange = useCallback((val: string) => {
        setSelectedTeam(val);
    }, []);

    const handleTabChange = useCallback((tab: FilterTab) => {
        setActiveTab(tab);
    }, []);

    // Fetch teams data and transform it on mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await callApi({
                    url: generateWidgetUrl(
                        "sport_cricket_series_teams_players_squad",
                        seriesKey,
                    ),
                    method: "GET",
                });
                if (response.isError) {
                    throw new Error("Network response was not ok");
                }
                const transformedData: TransformedTeam[] =
                    transformTeamsPlayers(response?.data?.result);
                setTeams(transformedData);
                if (transformedData.length > 0) {
                    // Use the first team as the default selected team
                    setSelectedTeam(
                        transformedData[0].team_name?.toLowerCase(),
                    );
                }
            } catch (err: any) {
                console.error(err.message || "Something went wrong");
            } finally {
                setLoading(false);
            }
        };
        if (seriesKey) fetchData();
    }, [seriesKey]);

    useEffect(() => {
        if (playerListContainerRef.current) {
            playerListContainerRef.current.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }
    }, [activeTab]);

    return (
        <div className={styles.cskTeamContainer} data-theme={theme}>
            <Dropdown
                options={dropdownOptions}
                selectedOption={selectedTeam}
                onChange={handleTeamChange}
            />

            <div className={styles["list-container"]} data-theme={theme}>
                <Tabs
                    tabs={[
                        FilterTab.All,
                        FilterTab.Batters,
                        FilterTab.Bowlers,
                        FilterTab.AllRounders,
                    ]}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />

                <div
                    className={styles["player-list-container"]}
                    ref={playerListContainerRef}
                >
                    {loading ? (
                        <div style={{ width: "100%" }}>
                            <Shimmer
                                theme={
                                    theme === Theme.DARK
                                        ? Theme.DARK
                                        : Theme.LIGHT
                                }
                                className={styles["shimmer"]}
                                width="100%"
                                height={
                                    dimensions?.width > 640 ? "33vh" : "600px"
                                }
                            />
                        </div>
                    ) : (
                        <PlayerList
                            players={filteredPlayers}
                            seriesName={seriesName}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default SquadWidget;
